import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { PRICING } from "@/data/content";
import { useConsult } from "@/context/ConsultContext";

export default function Pricing() {
  const { openConsult } = useConsult();
  return (
    <section id="pricing" className="py-24 lg:py-32 bg-[var(--dm-alt)] scroll-mt-20" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl mx-auto text-center">
          <span className="overline text-[var(--dm-purple)]">Pricing</span>
          <h2 className="font-head font-extrabold tracking-tighter text-4xl sm:text-5xl text-slate-900 mt-4 leading-[1.05]">
            Simple, transparent <span className="text-gradient">packages</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600">No hidden fees. Scale up or down anytime. Every plan is built to deliver ROI.</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {PRICING.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.1 }}
              className={`relative rounded-[1.75rem] p-8 flex flex-col ${
                p.highlighted ? "bg-[#0F172A] text-white shadow-2xl shadow-purple-200 lg:-translate-y-4 lg:scale-[1.02]" : "bg-white border border-slate-200 text-slate-900"
              }`}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
            >
              {p.highlighted && (
                <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-head font-bold text-white" style={{ background: "#F97316" }}>
                  <Sparkles size={13} /> Most Popular
                </span>
              )}
              <h3 className="font-head font-bold text-xl">{p.name}</h3>
              <p className={`mt-2 text-sm ${p.highlighted ? "text-slate-400" : "text-slate-500"}`}>{p.tagline}</p>
              <div className="mt-6 flex items-end gap-1">
                <span className="font-head font-extrabold text-4xl tracking-tight">{p.price}</span>
                <span className={`mb-1 ${p.highlighted ? "text-slate-400" : "text-slate-500"}`}>{p.period}</span>
              </div>

              <ul className="mt-7 space-y-3.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[0.95rem]">
                    <span className={`mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0 ${p.highlighted ? "bg-[var(--dm-orange)]" : "bg-purple-100"}`}>
                      <Check size={13} className={p.highlighted ? "text-white" : "text-[var(--dm-purple)]"} />
                    </span>
                    <span className={p.highlighted ? "text-slate-200" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={openConsult}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-8 rounded-full py-3.5 font-head font-bold transition-transform hover:-translate-y-0.5 ${
                  p.highlighted ? "text-slate-900 bg-white hover:shadow-lg" : "text-white hover:shadow-lg hover:shadow-purple-200"
                }`}
                style={p.highlighted ? {} : { background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
